import React, { useState, useEffect } from 'react';
import {
  Dialog,
  IconButton,
  CircularProgress,
  TextField,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CategoryIcon from '@mui/icons-material/Category';
import PaymentsIcon from '@mui/icons-material/Payments';
import InboxIcon from '@mui/icons-material/Inbox';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector } from 'react-redux';
import { ListPosSalesDetailsAPI } from '../../../component/Modules/PointOfSale/Report/ReportAPI';
import './mobileReports.css';

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const MobileSalesDetails = ({ open, onClose, branchId: propBranchId, branchName: propBranchName }) => {
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [expandedCategories, setExpandedCategories] = useState({});

  const decimalPosition = Number(localStorage.getItem("decimalPosition")) || 2;
  const userInfo = useSelector((state) => state.loginResponseSlice.value);
  const salesDetails = useSelector((state) => state.posReportshiftSlice.salesReport);

  // Use prop branchId if provided, otherwise fall back to userInfo
  const effectiveBranchId = propBranchId || userInfo?.branchPk;

  useEffect(() => {
    if (open && effectiveBranchId) {
      fetchSalesDetails();
    }
  }, [open, effectiveBranchId]);

  const fetchSalesDetails = async () => {
    if (!effectiveBranchId) return;
    
    setLoading(true);
    try {
      await ListPosSalesDetailsAPI({
        branchId: effectiveBranchId,
        fromDate: fromDate ? `${fromDate} 00:00:00` : undefined,
        endDate: toDate ? `${toDate} 23:59:59` : undefined
      });
    } catch (error) {
      console.error('Error fetching sales details:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toFixed(decimalPosition);
  };

  const renderProductDetails = () => {
    if (!salesDetails?.productDetails?.list?.length) return null;

    return (
      <>
        {salesDetails.productDetails.list.map((category, idx) => {
          const isExpanded = expandedCategories[category.categoryName] !== false;
          const categoryTotal = category.list?.reduce((sum, p) => sum + Number(p.amount || 0), 0) || 0;
          const categoryQty = category.list?.reduce((sum, p) => sum + Number(p.qty || 0), 0) || 0;

          return (
            <div className="mobile-report-card" key={idx}>
              <div 
                className="mobile-report-card-header category"
                onClick={() => toggleCategory(category.categoryName)}
              >
                <h4><CategoryIcon /> {category.categoryName}</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="total-badge">{formatAmount(categoryTotal)}</span>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </div>
              </div>
              <div className={`mobile-report-card-content ${isExpanded ? 'expanded' : ''}`}>
                <table className="mobile-report-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="text-center">Qty</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.list?.map((product, pIdx) => (
                      <tr key={pIdx}>
                        <td>{product.productName}</td>
                        <td className="text-center">{product.qty}</td>
                        <td className="text-right">{formatAmount(product.amount)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td>Total</td>
                      <td className="text-center">{categoryQty}</td>
                      <td className="text-right">{formatAmount(categoryTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderPaymentSummary = () => {
    if (!salesDetails?.paymentSummary?.list?.length) return null;

    const total = salesDetails.paymentSummary.list.reduce((sum, p) => sum + Number(p.amount || 0), 0);

    return (
      <div className="mobile-report-card">
        <div className="mobile-report-card-header payment">
          <h4><PaymentsIcon /> Payment Summary</h4>
          <span className="total-badge">{formatAmount(total)}</span>
        </div>
        <div className="mobile-report-card-content expanded">
          <table className="mobile-report-table">
            <thead>
              <tr>
                <th>Payment Method</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {salesDetails.paymentSummary.list.map((payment, idx) => (
                <tr key={idx}>
                  <td>{payment._id}</td>
                  <td className="text-right">{formatAmount(payment.amount)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>Total</td>
                <td className="text-right">{formatAmount(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderGrandTotals = () => {
    if (!salesDetails) return null;

    const productTotal = salesDetails.productDetails?.list?.reduce((sum, cat) => {
      return sum + (cat.list?.reduce((s, p) => s + Number(p.amount || 0), 0) || 0);
    }, 0) || 0;

    const paymentTotal = salesDetails.paymentSummary?.list?.reduce((sum, p) => sum + Number(p.amount || 0), 0) || 0;

    return (
      <div className="mobile-summary-grid">
        <div className="summary-item">
          <div className="label">Product Sales</div>
          <div className="value">{formatAmount(productTotal)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Payments</div>
          <div className="value">{formatAmount(paymentTotal)}</div>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="mobile-report-dialog"
    >
      <div className="mobile-report-header">
        <h3>Sales Details</h3>
        <IconButton className="close-btn" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <div className="mobile-report-container">
        {/* Filter Section */}
        <div className="mobile-report-filters">
          <div className="filter-row">
            <TextField
              label="From Date"
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To Date"
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className="filter-row" style={{ justifyContent: 'flex-end' }}>
            <Button
              className="submit-btn"
              onClick={fetchSalesDetails}
              disabled={loading}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="mobile-report-loading">
            <CircularProgress size={40} />
            <p>Loading sales details...</p>
          </div>
        ) : !salesDetails ? (
          <div className="mobile-report-empty">
            <InboxIcon />
            <p>No sales data found.<br />Try adjusting the date range.</p>
          </div>
        ) : (
          <>
            {renderGrandTotals()}
            {renderProductDetails()}
            {renderPaymentSummary()}
          </>
        )}
      </div>
    </Dialog>
  );
};

export default MobileSalesDetails;
